import { Component, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EventResponse, CancelEventResponse } from '../../../../../../../../@shared/models/response.module';
import { EventsService } from '../../../../../../../../@shared/services/events-service';
import { ToastService } from '../../../../../../../../@core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';

type CancelStep = 'warning' | 'confirm' | 'processing' | 'result';

@Component({
  selector: 'app-cancel-event-modal',
  standalone: false,
  templateUrl: './cancel-event-modal.html',
  styleUrl: './cancel-event-modal.css'
})
export class CancelEventModal {
  @Input() event: EventResponse;
  @Input() paidOrdersCount: number = 0;
  @Input() totalOrderAmount: number = 0;

  currentStep: CancelStep = 'warning';
  confirmationText: string = '';
  processing: boolean = false;
  cancelResult: CancelEventResponse | null = null;
  error: string | null = null;

  constructor(
    private modal: NgbActiveModal,
    private eventsService: EventsService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}

  get confirmationMatches(): boolean {
    const text = this.confirmationText.trim().toUpperCase();
    // Accept both English "CANCEL" and Norwegian "AVLYS"
    return text === 'CANCEL' || text === 'AVLYS';
  }

  nextStep() {
    if (this.currentStep === 'warning') {
      this.currentStep = 'confirm';
    } else if (this.currentStep === 'confirm' && this.confirmationMatches) {
      this.processCancellation();
    }
  }

  previousStep() {
    if (this.currentStep === 'confirm') {
      this.currentStep = 'warning';
      this.confirmationText = '';
    }
  }

  processCancellation() {
    this.currentStep = 'processing';
    this.processing = true;
    this.error = null;

    this.eventsService.cancelEvent(this.event.event_id).subscribe({
      next: (result: CancelEventResponse) => {
        this.cancelResult = result;
        this.processing = false;
        this.currentStep = 'result';
      },
      error: (err) => {
        this.processing = false;
        this.error = err?.error?.reason || this.translateService.instant('manageEvent.cancelEvent.error');
        this.currentStep = 'result';
      }
    });
  }

  formatAmount(cents: number): string {
    return (cents / 100).toFixed(2);
  }

  /**
   * Clean up Stripe error messages to be user-friendly
   */
  getCleanErrorMessage(errorMsg: string): string {
    if (!errorMsg) return this.translateService.instant('manageEvent.cancelModal.unknownError');

    // Check for "already been refunded" pattern
    if (errorMsg.toLowerCase().includes('already been refunded')) {
      return this.translateService.instant('manageEvent.cancelModal.errorAlreadyRefunded');
    }

    // Check for "charge has been disputed" pattern
    if (errorMsg.toLowerCase().includes('disputed')) {
      return this.translateService.instant('manageEvent.cancelModal.errorDisputed');
    }

    // Check for "insufficient funds" pattern
    if (errorMsg.toLowerCase().includes('insufficient')) {
      return this.translateService.instant('manageEvent.cancelModal.errorInsufficientFunds');
    }

    // Check for "no such charge" pattern
    if (errorMsg.toLowerCase().includes('no such charge') || errorMsg.toLowerCase().includes('not found')) {
      return this.translateService.instant('manageEvent.cancelModal.errorChargeNotFound');
    }

    // Default: return a generic message
    return this.translateService.instant('manageEvent.cancelModal.errorRefundFailed');
  }

  close(cancelled: boolean = false) {
    this.modal.close({ cancelled, result: this.cancelResult });
  }

  dismiss() {
    this.modal.dismiss();
  }
}
